import moment from "moment";

import { IEventSummary, IScore, IRunOff } from "@eagerdog/interfaces";
import { Constants, isUkcRallyShow, parseUkcRallyClass } from "@eagerdog/constants";

// Result labels + per-show-type display values are centralised in
// @eagerdog/scoring-core (single source of truth shared with mobile).
import { getResultLabel, getShowSpecificData } from "@eagerdog/scoring-core";

import { juniorArmBandClasses } from "src/utils/ArmBands";

let hasResults: string[] = [
  Constants.show_type.nosework,
  Constants.show_type.fast_cat,
  Constants.show_type.fast_dash,
  Constants.show_type.cat,
  Constants.show_type.fetch,
  Constants.show_type.urban_rat_race,
  Constants.show_type.country_rat_race,
  Constants.show_type.lost_and_found,
  Constants.show_type.rally_obedience,
  Constants.show_type.obedience,
  Constants.show_type.scentwork,
  Constants.show_type.dock_jumping,
  Constants.show_type.shed_dog,
  Constants.show_type.precision_coursing,
  Constants.show_type.weight_pull,
  Constants.show_type.farm_dog,
  Constants.show_type.water_dog,
  Constants.show_type.agility,
  Constants.show_type.herding,
  Constants.show_type.pre_trial_herding,
  Constants.show_type.instinct_test_herding,
  Constants.show_type.scent,
  Constants.show_type.scent_dog_games,
  Constants.show_type.barn_hunt,
];

const getDataRows = (scores: IScore[], event?: IEventSummary, runOffs?: IRunOff[]) => {
  let data = [];

  for (const i in scores) {
    const row: IScore = scores[i];

    // Use the score's checked_in field directly
    const checkedIn = row.checked_in || false;
    const complete = row.status === 'complete' || row.complete === true;

    // Use jr arm num condition
    const hasJuniorArmClass =
      juniorArmBandClasses.includes(row.dog_class?.show_element ?? "") && !!row.arm_number_junior;

    const isAkcObedience = event?.sanctioning_club === Constants.sanctioning_club.AKC && row.show_type === Constants.show_type.obedience;
    const isAkcRally = event?.sanctioning_club === Constants.sanctioning_club.AKC && row.show_type === Constants.show_type.rally_obedience;
    const isPreferredObedienceClass = (row.dog_class?.level || "").includes("Preferred");
    const catNotes = (row.cat as (typeof row.cat & { notes?: string }) | undefined)?.notes || "";
    const trialJumpHeight = isAkcObedience
      ? (isPreferredObedienceClass ? (row.akc_obedience_preferred_jump_height ?? row.obedience_jump_height) : row.obedience_jump_height)
      : (isAkcRally ? row.rally_jump_height : undefined);

    // UKC Rally: show Level RO1 + Section B whether stored as "RO1 B" or as level + section
    const ukcRallyClass = isUkcRallyShow(row.sanctioning_club ?? event?.sanctioning_club, row.show_type)
      ? parseUkcRallyClass(row.dog_class?.level, row.dog_class?.section)
      : null;

    data.push({
      show_id: row.show_id,
      dog_id: row.dog_id,
      eventId: row.event_id, // Add eventId for check-in functionality
      eventTitle: row.event_title,
      eventHandle: row.event_handle,
      eventDate: moment(row.event_start_date).format("MM/DD/YYYY"),
      show: row.show_type + " " + row.show_name,
      showType: row.show_type,
      showName: row.show_name,
      sanctioningClub: row.sanctioning_club,
      showDate: moment(row.show_date).format("MM/DD/YYYY"),
      armband: hasJuniorArmClass ? row.arm_number_junior : row.arm_number,
      callName: `${row.call_name} (${row.dog_sex === "Male" ? "D" : "B"})`,
      breedGroup: row.breed_group,
      breed: row.breed,
      registeredName: row.registered_name,
      ownerName: row.owner_name,
      judge: row.judge_name || "",
      result: getResultLabel(row),
      notes: row.show_type === Constants.show_type.cat ? catNotes : "",
      conflictNote: row.conflict_note || "",
      element: row.dog_class?.show_element,
      level: row.dog_class?.level || "",
      groupClass: row.dog_class?.group_class || "",
      timeSlot: row.time_slot || "",
      status: row.status,
      checkedIn: checkedIn,
      complete: complete,
      scoreId: row._id,
      jump_height: row.agility_jump_height ? `${row.agility_jump_height}"` : "",
      trialJumpHeight: trialJumpHeight !== undefined && trialJumpHeight !== null ? `${trialJumpHeight}"` : "",
      ...getShowSpecificData(row.show_type, row, scores, event, runOffs),
      ...(ukcRallyClass ? { level: ukcRallyClass.level, section: ukcRallyClass.section } : {}),
      ...getActions(event, row),
    });
  }

  return data;
}

const getActions = (event: IEventSummary | undefined, row: any) => {
  if (!event) {
    return {};
  }

  // Determine if individual scoresheet downloads are supported
  const canDownloadIndividualResults = 
    // AKC Fetch
    (row.show_type === Constants.show_type.fetch && event.sanctioning_club === Constants.sanctioning_club.AKC) ||
    // UKC Nosework, Obedience, Rally Obedience
    ((row.show_type === Constants.show_type.nosework || 
      row.show_type === Constants.show_type.obedience || 
      row.show_type === Constants.show_type.rally_obedience) && 
     event.sanctioning_club === Constants.sanctioning_club.UKC);

  return {
    actions: {
      result: row,
      event: event,
      showId: row.show_id,
      canEnterResults: hasResults.includes(row.show_type),
      canDownloadIndividualResults,
    }
  }
}

export { getDataRows }
